// Admin Billing & Tax — /admin/billing, permission: manage_settings
// Multi-country overview of tax rate/label (read-only here — edit on Country
// Pricing) plus per-country billing display fields (business name, tax ID,
// invoice prefix). India keeps its own dedicated, GSTIN/PAN-validated flow at
// Settings → GST & Billing as the real source of truth for its tax-invoice
// engine — this page shows it read-only and links out to it rather than
// duplicating it. See `GET/PUT /admin/settings/tax` in
// backend/routes/country_pricing.py.
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Receipt, Loader2, ExternalLink } from 'lucide-react';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const COUNTRY_FLAGS = {
  IN: '🇮🇳', AU: '🇦🇺', US: '🇺🇸', GB: '🇬🇧', SG: '🇸🇬', AE: '🇦🇪',
  CA: '🇨🇦', NZ: '🇳🇿', MY: '🇲🇾', DE: '🇩🇪', FR: '🇫🇷', JP: '🇯🇵',
};

export const AdminBillingTax = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState('IN');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState({});

  const fetchTax = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/admin/settings/tax`, { headers });
      setCountries(data);
      const byCode = {};
      data.forEach((c) => { byCode[c.country_code] = c; });
      setDrafts(byCode);
      if (data.length && !data.some((c) => c.country_code === selected)) {
        setSelected(data[0].country_code);
      }
    } catch (e) {
      toast.error('Failed to load tax settings');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchTax(); }, [fetchTax]);

  const current = drafts[selected] || {};
  const updateField = (key, value) => {
    setDrafts((prev) => ({ ...prev, [selected]: { ...prev[selected], [key]: value } }));
  };

  const handleSave = async () => {
    if (selected === 'IN') return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/settings/tax/${selected}`, {
        business_name: current.business_name || '',
        tax_id: current.tax_id || '',
        tax_id_label: current.tax_id_label || '',
        invoice_prefix: current.invoice_prefix || '',
      }, { headers });
      toast.success(`${current.country_name || selected} tax settings saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div style={{ padding: 0, maxWidth: 720 }} data-testid="admin-billing-tax">
      <div className="mb-8 flex items-center gap-3">
        <Receipt className="w-7 h-7 text-[#A67C52]" />
        <div>
          <h1 className="text-3xl font-serif font-medium">Billing & Tax</h1>
          <p className="text-muted-foreground">
            Invoicing and tax details per country. Tax rates are managed on{' '}
            <Link to="/admin/country-pricing" className="underline text-[#A67C52]">Country Pricing</Link>.
          </p>
        </div>
      </div>

      {/* Country tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {countries.map((c) => (
          <button
            key={c.country_code}
            onClick={() => setSelected(c.country_code)}
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              border: '2px solid',
              borderColor: selected === c.country_code ? '#A67C52' : 'var(--border, #e5e7eb)',
              background: selected === c.country_code ? '#fdf8f4' : 'transparent',
              color: selected === c.country_code ? '#A67C52' : 'inherit',
              fontWeight: selected === c.country_code ? 700 : 400,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            {COUNTRY_FLAGS[c.country_code] || '🌍'} {c.country_name} ({c.tax_label} {c.tax_rate}%)
          </button>
        ))}
      </div>

      {/* Tax rate info (read-only, edit in Country Pricing) */}
      <div style={{
        background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
        padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#92400e',
      }}>
        ℹ️ <strong>{current.tax_label} rate: {current.tax_rate}%</strong>
        {' '}— Edit tax rate on{' '}
        <Link to="/admin/country-pricing" style={{ color: '#A67C52' }}>Country Pricing →</Link>
      </div>

      {selected === 'IN' ? (
        <div style={{
          border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, padding: 20,
        }}>
          <p className="text-sm text-muted-foreground mb-3">
            India's tax details (GSTIN, PAN, invoice numbering) use the dedicated, validated
            GST flow — this tab shows the current values read-only.
          </p>
          <div className="space-y-2 text-sm">
            <div><strong>Business Name:</strong> {current.business_name || '—'}</div>
            <div><strong>{current.tax_id_label || 'GSTIN'}:</strong> {current.tax_id || '—'}</div>
            <div><strong>Invoice Prefix:</strong> {current.invoice_prefix || '—'}</div>
          </div>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/admin/gst-settings">
              <ExternalLink className="w-4 h-4 mr-2" /> Edit in GST & Billing
            </Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold mb-1 block">Business Legal Name</Label>
            <Input
              value={current.business_name || ''}
              onChange={(e) => updateField('business_name', e.target.value)}
              placeholder="Thachari Interior Training Pvt Ltd"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">{current.tax_id_label || 'Tax ID'}</Label>
            <Input
              value={current.tax_id || ''}
              onChange={(e) => updateField('tax_id', e.target.value)}
              placeholder={selected === 'AU' ? '12 345 678 901' : ''}
            />
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">Tax ID Label</Label>
            <Input
              value={current.tax_id_label || ''}
              onChange={(e) => updateField('tax_id_label', e.target.value)}
              placeholder="ABN / VAT Number / EIN"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold mb-1 block">Invoice Number Prefix</Label>
            <Input
              value={current.invoice_prefix || ''}
              onChange={(e) => updateField('invoice_prefix', e.target.value)}
              placeholder={`THAC-${selected}`}
            />
          </div>

          <Button onClick={handleSave} disabled={saving} className="bg-[#A67C52] hover:bg-[#8f6a45] text-white">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {saving ? 'Saving...' : `Save ${current.country_name || selected} Tax Settings`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminBillingTax;
